import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5200',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('auth_token');
      delete api.defaults.headers.common['Authorization'];
      
      // Redirect to login if not already on auth page
      if (window.location.pathname !== '/ai-assistant') {
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API endpoints
export const authApi = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  registerWithInvitation: async (userData) => {
    const response = await api.post('/auth/register-with-invitation', userData);
    return response.data;
  },

  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    const response = await api.post('/auth/change-password', {
      CurrentPassword: currentPassword,
      NewPassword: newPassword,
      ConfirmPassword: confirmPassword
    });
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (email, token, newPassword, confirmPassword) => {
    const response = await api.post('/auth/reset-password', {
      Email: email,
      Token: token,
      NewPassword: newPassword,
      ConfirmPassword: confirmPassword
    });
    return response.data;
  }
};

// Admin API endpoints
export const adminApi = {
  // User management
  getUsers: async (params = {}) => {
    const response = await api.get('/admin/users', { params });
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '0')
    };
  },

  getUser: async (id) => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },

  updateUser: async (id, updates) => {
    const response = await api.put(`/admin/users/${id}`, updates);
    return response.data;
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  getAdminStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  // Get user's app access (for admin management)
  getUserAppAccess: async (userId) => {
    const response = await api.get(`/appmodule/user/${userId}/apps`);
    return response.data;
  },

  // SAM.gov API key (encrypted at rest, rotatable by admin)
  getSamApiKeyStatus: async () => {
    const response = await api.get('/admin/sam-api-key/status');
    return response.data;
  },

  setSamApiKey: async (apiKey) => {
    await api.post('/admin/sam-api-key', { apiKey });
  }
};

// Invitation API endpoints
export const invitationApi = {
  sendInvitation: async (email, role = 'User') => {
    const response = await api.post('/invitation/send', { email, role });
    return response.data;
  },

  getInvitations: async (params = {}) => {
    const response = await api.get('/invitation', { params });
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '0')
    };
  },

  revokeInvitation: async (id) => {
    const response = await api.post(`/invitation/${id}/revoke`);
    return response.data;
  },

  resendInvitation: async (id) => {
    const response = await api.post(`/invitation/${id}/resend`);
    return response.data;
  },

  validateInvitation: async (token) => {
    const response = await api.post('/invitation/validate', { token });
    return response.data;
  }
};

// Opportunities API endpoints
export const opportunitiesApi = {
  // Get opportunities with filtering and pagination
  getOpportunities: async (params = {}) => {
    const response = await api.get('/opportunities', { 
      params,
      paramsSerializer: function(params) {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
          if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(key, v));
          } else {
            searchParams.append(key, value);
          }
        }
        return searchParams.toString();
      }
    });
    return response.data;
  },

  // Get single opportunity by ID
  getOpportunity: async (id) => {
    const response = await api.get(`/opportunities/${id}`);
    return response.data;
  },

  // Get filter options for dropdowns
  getFilterOptions: async () => {
    const response = await api.get('/opportunities/filters');
    return response.data;
  },

  // Export opportunities to CSV
  exportOpportunities: async (params = {}) => {
    // Remove pagination parameters from export (export should get all results)
    const exportParams = { ...params };
    delete exportParams.limit;
    delete exportParams.offset;
    
    const response = await api.get('/opportunities/export/csv', { 
      params: exportParams,
      responseType: 'blob',
      paramsSerializer: function(params) {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
          if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(key, v));
          } else {
            searchParams.append(key, value);
          }
        }
        return searchParams.toString();
      }
    });
    return response.data;
  },

  // Get export count
  getExportCount: async (params = {}) => {
    const response = await api.get('/opportunities/export/count', { 
      params,
      paramsSerializer: function(params) {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
          if (Array.isArray(value)) {
            value.forEach(v => searchParams.append(key, v));
          } else {
            searchParams.append(key, value);
          }
        }
        return searchParams.toString();
      }
    });
    return response.data;
  },
};

// User Dashboard API endpoints
export const userDashboardApi = {
  // User profile
  getUserProfile: async () => {
    const response = await api.get('/user/profile');
    return response.data;
  },
  
  updateUserProfile: async (updates) => {
    const response = await api.put('/user/profile', updates);
    return response.data;
  },

  // Watchlist/Favorites
  getWatchlist: async (params = {}) => {
    const response = await api.get('/user/watchlist', { params });
    return response.data;
  },
  
  addToWatchlist: async (opportunityNoticeId, data = {}) => {
    const response = await api.post(`/user/watchlist/${opportunityNoticeId}`, data);
    return response.data;
  },
  
  removeFromWatchlist: async (opportunityNoticeId) => {
    const response = await api.delete(`/user/watchlist/${opportunityNoticeId}`);
    return response.data;
  },

  updateWatchlistItem: async (opportunityNoticeId, updates) => {
    const response = await api.put(`/user/watchlist/${opportunityNoticeId}`, updates);
    return response.data;
  },

  // Saved Searches
  getSavedSearches: async () => {
    const response = await api.get('/user/saved-searches');
    return response.data;
  },
  
  saveSearch: async (searchData) => {
    const response = await api.post('/user/saved-searches', searchData);
    return response.data;
  },
  
  updateSavedSearch: async (id, updates) => {
    const response = await api.put(`/user/saved-searches/${id}`, updates);
    return response.data;
  },
  
  deleteSavedSearch: async (id) => {
    const response = await api.delete(`/user/saved-searches/${id}`);
    return response.data;
  },

  executeSavedSearch: async (id) => {
    const response = await api.post(`/user/saved-searches/${id}/execute`);
    return response.data;
  },

  // Recommendations
  getRecommendations: async (params = {}) => {
    try {
      const response = await api.get('/user/recommendations', { params });
      return response.data;
    } catch (error) {
      console.error('API: Recommendations error:', error.response?.status, error.response?.data, error.message);
      throw error;
    }
  },

  dismissRecommendation: async (id) => {
    const response = await api.post(`/user/recommendations/${id}/dismiss`);
    return response.data;
  },

  generateRecommendations: async () => {
    try {
      const response = await api.post('/user/recommendations/generate');
      return response.data;
    } catch (error) {
      console.error('API: Recommendation generation error:', error.response?.status, error.response?.data, error.message);
      throw error;
    }
  },

  // Activity History
  getActivityHistory: async (params = {}) => {
    const response = await api.get('/user/dashboard/activity', { params });
    return response.data;
  },

  // Dashboard Statistics
  getDashboardStats: async () => {
    const response = await api.get('/user/dashboard/stats');
    return response.data;
  }
};

// Entity API endpoints
export const entityApi = {
  validateUei: async (uei) => {
    const response = await api.get(`/entity/validate/${uei}`);
    return response.data;
  },

  lookupUei: async (uei) => {
    const response = await api.get(`/entity/lookup/${uei}`);
    return response.data;
  },

  lookupMultipleUeis: async (ueis) => {
    const response = await api.post('/entity/lookup/batch', { ueis });
    return response.data;
  }
};

// UserRegistration API endpoints
export const userRegistrationApi = {
  // Get all user registrations
  getUserRegistrations: async () => {
    const response = await api.get('/UserRegistration');
    return response.data;
  },

  // Create a new user registration
  createUserRegistration: async (registrationData) => {
    const response = await api.post('/UserRegistration', registrationData);
    return response.data;
  },

  // Update an existing user registration
  updateUserRegistration: async (id, updates) => {
    const response = await api.put(`/UserRegistration/${id}`, updates);
    return response.data;
  },

  // Delete a user registration
  deleteUserRegistration: async (id) => {
    const response = await api.delete(`/UserRegistration/${id}`);
    return response.data;
  },

  // Get a specific user registration by ID
  getUserRegistration: async (id) => {
    const response = await api.get(`/UserRegistration/${id}`);
    return response.data;
  }
};

// Past Performance API endpoints
export const pastPerformanceApi = {
  // Get past performance summary for specific UEI
  getPastPerformanceByUei: async (uei) => {
    const response = await api.get(`/pastperformance/uei/${uei}`);
    return response.data;
  },

  // Get basic recipient info for UEI
  getRecipientInfo: async (uei) => {
    const response = await api.get(`/pastperformance/recipient/${uei}`);
    return response.data;
  },

  // Search contractors
  searchContractors: async (params = {}) => {
    const response = await api.get('/pastperformance/search', { params });
    return response.data;
  },

  // Bulk performance analysis for multiple UEIs
  getBulkPerformance: async (ueis) => {
    const response = await api.post('/pastperformance/performance/batch', { ueis });
    return response.data;
  },

  // Compare two contractors
  compareContractors: async (uei1, uei2) => {
    const response = await api.get('/pastperformance/performance/compare', {
      params: { uei1, uei2 }
    });
    return response.data;
  },

  // Get past performance data for user's registered entities
  getUserEntitiesPerformance: async () => {
    // Get user's registered entities first
    const registrations = await userRegistrationApi.getUserRegistrations();
    const ueis = registrations.map(reg => reg.ueiSam || reg.UeiSam).filter(Boolean);
    
    if (ueis.length === 0) {
      return {};
    }
    
    // Use batch API to get performance for all user entities
    const rawAwards = await pastPerformanceApi.getBulkPerformance(ueis);
    
    // Transform backend response into performance summary by UEI
    const performanceData = {};
    
    if (Array.isArray(rawAwards) && rawAwards.length > 0) {
      rawAwards.forEach(response => {
        if (response.success && response.pastPerformance) {
          const perf = response.pastPerformance;
          const uei = response.uei;
          
          const totalContracts = perf.totalAwards || 0;
          const totalValue = perf.totalAwardAmount || 0;
          const avgContractValue = totalContracts > 0 ? totalValue / totalContracts : 0;
          
          const entityName = perf.recipientName || 'Unknown Entity';
          
          // Transform recent awards into our format
          const recentContracts = (perf.recentAwards || [])
            .slice(0, 5)
            .map((award, index) => ({
              id: award['Award ID'] || `award-${index}`,
              title: award['Award Description'] || 'Contract Award',
              agency: award['Awarding Agency'] || 'Unknown Agency',
              value: parseFloat(award['Award Amount']) || 0,
              startDate: award['Start Date'],
              endDate: award['End Date'],
              status: new Date(award['End Date']) > new Date() ? 'Active' : 'Completed'
            }));
          
          // Use NAICS data from backend if available, otherwise calculate from recent awards
          let naicsPerformance = {};
          if (perf.topNaicsCodes && perf.topNaicsCodes.length > 0) {
            perf.topNaicsCodes.forEach(naicsCode => {
              naicsPerformance[naicsCode] = { 
                contracts: Math.floor(totalContracts / perf.topNaicsCodes.length), 
                totalValue: Math.floor(totalValue / perf.topNaicsCodes.length) 
              };
            });
          } else {
            // Calculate from recent awards if no top NAICS codes
            (perf.recentAwards || []).forEach(award => {
              const naicsCode = award['NAICS Code'];
              if (naicsCode) {
                if (!naicsPerformance[naicsCode]) {
                  naicsPerformance[naicsCode] = { contracts: 0, totalValue: 0 };
                }
                naicsPerformance[naicsCode].contracts++;
                naicsPerformance[naicsCode].totalValue += parseFloat(award['Award Amount']) || 0;
              }
            });
          }
          
          performanceData[uei] = {
            entityName,
            totalContracts,
            totalValue,
            avgContractValue,
            recentContracts,
            naicsPerformance
          };
        }
      });
    }
    
    return performanceData;
  }
};

// Contract Intelligence API endpoints
export const contractIntelligenceApi = {
  // Get contract intelligence data by award ID
  getContractIntelligence: async (awardId) => {
    const response = await api.get(`/contractintelligence/${awardId}`);
    return response.data;
  },

  // Get contract burn rate analysis
  getBurnRateAnalysis: async (awardId) => {
    const response = await api.get(`/contractintelligence/${awardId}/burnrate`);
    return response.data;
  },

  // Get contract recommendations
  getContractRecommendations: async (awardId) => {
    const response = await api.get(`/contractintelligence/${awardId}/recommendations`);
    return response.data;
  },

  // Get contract pattern analysis
  getContractPatterns: async (awardId) => {
    const response = await api.get(`/contractintelligence/${awardId}/patterns`);
    return response.data;
  },

  // Get opportunity intelligence data
  getOpportunityIntelligence: async (agency, naicsCode, setAside = null) => {
    const params = new URLSearchParams({
      agency,
      naicsCode,
      ...(setAside && { setAside })
    });
    
    const response = await api.get(`/contractintelligence/opportunity-intelligence?${params}`);
    return response.data;
  },

  // Test contract intelligence API
  testIntelligenceAPI: async () => {
    const response = await api.get('/contractintelligence/test');
    return response.data;
  }
};

// Helper function to build query parameters
export const buildQueryParams = (filters) => {
  const params = {};
  
  
  // Add filters only if they have values
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '' && 
        !(Array.isArray(value) && value.length === 0)) {
      // Handle date fields - convert to ISO string if it's a date
      if (key.includes('Date') || key.includes('From') || key.includes('To')) {
        if (value instanceof Date) {
          params[key] = value.toISOString();
        } else if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Convert YYYY-MM-DD to ISO string
          params[key] = new Date(value + 'T00:00:00.000Z').toISOString();
        } else {
          params[key] = value;
        }
      } else if (Array.isArray(value)) {
        // Handle arrays - send as arrays for proper backend handling
        // Convert camelCase to PascalCase for backend compatibility
        const backendKey = key === 'naicsCodes' ? 'NaicsCodes' : key;
        params[backendKey] = value;
      } else {
        params[key] = value;
      }
    }
  });
  
  return params;
};

// Executive AI Assistant API endpoints
export const executiveAiApi = {
  // Generate executive summary for opportunity
  generateExecutiveSummary: async (opportunityId, companyUei = null) => {
    const response = await api.post('/aiagent/executive-summary', {
      opportunityId,
      companyUei
    });
    return response.data;
  },

  // Get general AI assistant response (existing functionality)
  askQuestion: async (question) => {
    const response = await api.post('/aiagent/ask', {
      question
    });
    return response.data;
  },

  // Get AI assistant examples
  getExamples: async () => {
    const response = await api.get('/aiagent/examples');
    return response.data;
  }
};

export default api;